import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, TableSortLabel, TablePagination, Chip, Button,
  Alert, IconButton, TextField, MenuItem, Collapse, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider, Checkbox,
  Tooltip, CircularProgress, Snackbar
} from '@mui/material';
import {
  Home, Edit, Delete, Receipt, Payment, FilterList, Search,
  Download, AppRegistration, AccountBalanceWallet, School,
  Person, FamilyRestroom, LocationOn, MenuBook, Add, Close,
  RequestQuote, CheckCircle, Settings, Scoreboard, BarChart, Refresh, Analytics
} from '@mui/icons-material';
import axios from 'axios';
import LearnersForm from './LearnersForm';
import { fmt, sym, locale } from '../utils/currency';
import { subjectAppliesToPathway } from '../constants/pathways';
import { canView } from '../utils/permissions';

const API = process.env.REACT_APP_API_URL;

const InfoRow = ({ label, value }) => (
  <Box display="flex" gap={1} mb={0.5}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160, fontWeight: 500 }}>{label}</Typography>
    <Typography variant="body2">{value || <em style={{ color: '#bbb' }}>—</em>}</Typography>
  </Box>
);

const SectionHead = ({ icon: Icon, label }) => (
  <Box display="flex" alignItems="center" gap={1} mt={2} mb={0.5}>
    <Icon fontSize="small" color="primary" />
    <Typography variant="subtitle2" color="primary">{label}</Typography>
  </Box>
);

// ── Learner Card Dialog ───────────────────────────────────────────────────────
const LearnerCard = ({ learner, stages, onClose, onEdit, onDelete, onBill, onPay, onTermReg, onResults, onAnalytics, onManageGuardians }) => {
  if (!learner) return null;
  const stageName = stages.find(s => s.code === learner.stage)?.name || learner.stage;
  const balance   = learner.balance ?? 0;

  return (
    <>
      <DialogTitle component="div" sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Person color="primary" />
          <Box flex={1}>
            <Typography variant="h6" lineHeight={1}>{learner.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {learner.admNo} · {stageName} · {learner.studentType || '—'}
            </Typography>
          </Box>
          <Chip
            label={`Balance: ${sym()} ${fmt(balance)}`}
            color={balance > 0 ? 'error' : 'success'}
            size="small" variant="outlined"
          />
        </Box>
      </DialogTitle>

      {/* Primary actions — kept at the top for quick access */}
      <Box sx={{ px: 2, pb: 1.5, pt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.75,
        borderBottom: 1, borderColor: 'divider' }}>
        <Button size="small" variant="contained" startIcon={<AppRegistration />} onClick={onTermReg}>
          Term Registration
        </Button>
        <Button size="small" variant="outlined" startIcon={<BarChart />} onClick={() => onResults(learner)}
          sx={{ color: '#004d40', borderColor: '#00695c' }}>
          Results
        </Button>
        <Button size="small" variant="outlined" startIcon={<Analytics />} onClick={() => onAnalytics(learner)}
          sx={{ color: '#00695c', borderColor: '#4db6ac' }}>
          Analytics
        </Button>
        <Button size="small" variant="outlined" startIcon={<AccountBalanceWallet />} onClick={() => onPay(learner.admNo)}>
          Payment
        </Button>
        <Button size="small" variant="outlined" startIcon={<Receipt />} onClick={() => onBill(learner.admNo, learner.stage)}>
          Bill
        </Button>
      </Box>

      <DialogContent dividers>
        {/* Personal */}
        <SectionHead icon={Person} label="Personal Details" />
        <InfoRow label="Date of Birth"  value={learner.dob ? new Date(learner.dob).toLocaleDateString(locale()) : ''} />
        <InfoRow label="Gender"         value={learner.gender} />
        <InfoRow label="Nationality"    value={learner.nationality} />
        <InfoRow label="Denomination"   value={learner.denomination} />
        <InfoRow label="Student Type"   value={learner.studentType} />

        {/* Admission */}
        <SectionHead icon={School} label="Admission" />
        <InfoRow label="Admission No"   value={learner.admNo} />
        <InfoRow label="Admission Date" value={learner.admDate ? new Date(learner.admDate).toLocaleDateString(locale()) : ''} />
        <InfoRow label="Current Stage"  value={`${learner.stage} — ${stageName}`} />
        <InfoRow label="Previous School"value={learner.previousSchool} />
        <InfoRow label="Grade 6 District" value={learner.grade6District} />

        {/* Location */}
        <SectionHead icon={LocationOn} label="Address" />
        <InfoRow label="Home District"  value={learner.homeDistrict} />
        <InfoRow label="Home Address"   value={learner.homeAddress} />

        <Divider sx={{ my: 1.5 }} />

        {/* Parents */}
        <SectionHead icon={FamilyRestroom} label="Parent / Guardian" />
        {learner.fatherName && (
          <>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Father</Typography>
            <InfoRow label="Name"       value={learner.fatherName} />
            <InfoRow label="Phone"      value={learner.fatherPhone} />
            <InfoRow label="Occupation" value={learner.fatherOccupation} />
            <InfoRow label="Email"      value={learner.fatherEmail} />
          </>
        )}
        {learner.motherName && (
          <>
            <Typography variant="caption" color="text.secondary" display="block" mt={1} mb={0.5}>Mother</Typography>
            <InfoRow label="Name"       value={learner.motherName} />
            <InfoRow label="Phone"      value={learner.motherPhone} />
            <InfoRow label="Occupation" value={learner.motherOccupation} />
            <InfoRow label="Email"      value={learner.motherEmail} />
          </>
        )}
        {learner.guardianName && (
          <>
            <Typography variant="caption" color="text.secondary" display="block" mt={1} mb={0.5}>Guardian</Typography>
            <InfoRow label="Name"         value={learner.guardianName} />
            <InfoRow label="Phone"        value={learner.guardianPhone} />
            <InfoRow label="Relationship" value={learner.guardianRelationship} />
          </>
        )}
        {!learner.fatherName && !learner.motherName && !learner.guardianName && (
          <Typography variant="body2" color="text.secondary">No parent/guardian details recorded.</Typography>
        )}
        {/* Clickable link into the learner's Guardians manager (add / link contacts) */}
        <Button size="small" startIcon={<Add />} onClick={onManageGuardians}
          sx={{ mt: 0.5, textTransform: 'none' }}>
          {(!learner.fatherName && !learner.motherName && !learner.guardianName)
            ? 'Add a guardian'
            : 'Manage guardians'}
        </Button>

        <Divider sx={{ my: 1.5 }} />

        {/* Contact */}
        <InfoRow label="Contact Email"  value={learner.parentEmail} />
      </DialogContent>

      <DialogActions sx={{ flexWrap: 'wrap', gap: 0.5, px: 2 }}>
        <Button size="small" startIcon={<Edit />} onClick={onEdit}>Edit</Button>
        <Button size="small" startIcon={<Delete />} color="error"
          onClick={() => onDelete(learner)}>Delete</Button>
        <Box flex={1} />
        <Button size="small" variant="outlined" onClick={onClose}>Close</Button>
      </DialogActions>
    </>
  );
};

// ── Term Registration Dialog ──────────────────────────────────────────────────
const TermRegDialog = ({ open, learner, onClose }) => {
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const [registrations, setRegistrations] = useState([]);
  const [years,         setYears]         = useState([]);
  const [allAreas,      setAllAreas]      = useState([]);
  const [stageAreas,    setStageAreas]    = useState([]); // assignments for this learner's stage
  const [templates,     setTemplates]     = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [creating,      setCreating]      = useState(false);
  const [saving,        setSaving]        = useState(false);
  const [error,         setError]         = useState('');
  const [popup,         setPopup]         = useState('');   // prominent toast for server errors
  const [okMsg,         setOkMsg]         = useState('');   // success toast (e.g. auto-applied credit)
  const [viewAreas,     setViewAreas]     = useState(null);

  // Per-learner learning-area editor (for an existing registration): add/remove
  // optional subjects while the term registration is still open (status Active).
  const [editStageAreas, setEditStageAreas] = useState([]); // stage subjects for the viewed reg
  const [editSelected,   setEditSelected]   = useState([]); // currently selected area codes
  const [savingAreas,    setSavingAreas]    = useState(false);
  const [areasErr,       setAreasErr]       = useState('');

  // Generate invoice state
  const [learnerInvoices, setLearnerInvoices] = useState([]);
  const [invoicedTerms,   setInvoicedTerms]   = useState(new Set());
  const [, setGlAccounts] = useState([]);
  const [genOpen,       setGenOpen]       = useState(false);
  const [genReg,        setGenReg]        = useState(null);
  const [, setGenGlAccount] = useState('');
  const [generating,    setGenerating]    = useState(false);

  // New registration form state
  const [newForm, setNewForm] = useState({
    academicYear: '', term: '', selectedAreas: []
  });

  // Confirm-delete registration dialog
  const [deleteRegDlg, setDeleteRegDlg] = useState(null); // stores registration object/id

  const fetchData = useCallback(async () => {
    if (!learner) return;
    setLoading(true);
    setError('');
    const G = (url) => axios.get(`${API}${url}`, { headers });
    // Resilient load: one failing call (e.g. a permission-gated finance endpoint)
    // must not blank the whole dialog. Only the core registrations are required.
    const [regRes, yRes, aRes, tRes, invRes, glRes] = await Promise.allSettled([
      G(`/api/academics/term-registrations?admNo=${learner.admNo}`),
      G(`/api/academics/years`),
      G(`/api/academics/learning-areas`),
      G(`/api/academics/fee-templates`),
      G(`/api/finance/invoices?admNo=${learner.admNo}`),
      G(`/api/finance/gl-accounts`),
    ]);
    // Normalise to an array whether the endpoint returns [...] or { data: [...] }
    const arr = (r) => {
      if (r.status !== 'fulfilled') return [];
      const d = r.value.data;
      if (Array.isArray(d)) return d;
      if (Array.isArray(d?.data)) return d.data;
      return [];
    };

    if (regRes.status === 'fulfilled') {
      setRegistrations(arr(regRes));
    } else {
      setError('Failed to load term registrations');
    }
    setYears(arr(yRes));
    setAllAreas(arr(aRes).filter(a => a.isActive));
    setTemplates(arr(tRes));

    const active = arr(invRes).filter(i => i.status !== 'reversed');
    setLearnerInvoices(active);
    setInvoicedTerms(new Set(active.map(i => i.reason).filter(Boolean)));

    setGlAccounts(arr(glRes).filter(g => g.accountType === 'Revenue' || g.accountType === 'Income'));

    setLoading(false);
  }, [learner]); // eslint-disable-line

  useEffect(() => {
    if (open) { fetchData(); setCreating(false); setNewForm({ academicYear: '', term: '', selectedAreas: [] }); setError(''); }
  }, [open, fetchData]);

  const matchedTemplate = useMemo(() => {
    if (!newForm.academicYear || !newForm.term || !learner) return null;
    const st = learner.studentType || 'Boarder';
    return templates.find(t =>
      t.year === newForm.academicYear &&
      t.stageCode === learner.stage &&
      t.term === parseInt(newForm.term, 10) &&
      (t.studentType === st || t.studentType === 'All')
    );
  }, [newForm.academicYear, newForm.term, learner, templates]);

  // Per-term balance map: key = "year/Term N" → { billed, paid }
  const termBalanceMap = useMemo(() => {
    const map = {};
    learnerInvoices.forEach(inv => {
      if (!inv.reason) return;
      if (!map[inv.reason]) map[inv.reason] = { billed: 0, paid: 0 };
      map[inv.reason].billed += inv.amount || 0;
      map[inv.reason].paid   += inv.paid   || 0;
    });
    return map;
  }, [learnerInvoices]);

  // Fetch stage learning areas when year or learner changes
  useEffect(() => {
    if (!newForm.academicYear || !learner?.stage) { setStageAreas([]); return; }
    axios.get(
      `${API}/api/academics/stage-learning-areas?year=${newForm.academicYear}&stageCode=${learner.stage}`,
      { headers }
    ).then(res => {
      // Only offer subjects that apply to this learner's pathway: Common
      // subjects plus the ones tagged for their pathway (a STEM learner sees
      // STEM options, not Social-Sciences ones). Learners with no pathway set
      // get Common-only.
      const applicable = (res.data || []).filter(a => subjectAppliesToPathway(a.pathway, learner.pathway));
      setStageAreas(applicable);
    })
     .catch(() => setStageAreas([]));
  }, [newForm.academicYear, learner]); // eslint-disable-line

  // Auto-select mandatory areas whenever stage areas load or term is chosen
  useEffect(() => {
    if (!newForm.term) return;
    const mandatoryCodes = stageAreas.filter(a => a.isMandatory).map(a => a.areaCode);
    setNewForm(f => ({ ...f, selectedAreas: [...new Set(mandatoryCodes)] }));
  }, [stageAreas, newForm.term]); // eslint-disable-line

  // When an existing registration is opened for editing, load its stage's
  // subjects (pathway-filtered) and seed the selection from what's registered.
  useEffect(() => {
    if (!viewAreas || !learner?.stage) { setEditStageAreas([]); setEditSelected([]); setAreasErr(''); return; }
    setAreasErr('');
    setEditSelected((viewAreas.learningAreas || []).map(a => a.areaCode));
    axios.get(
      `${API}/api/academics/stage-learning-areas?year=${viewAreas.academicYear}&stageCode=${learner.stage}`,
      { headers }
    ).then(res => {
      const applicable = (res.data || []).filter(a =>
        subjectAppliesToPathway(a.pathway, viewAreas.pathway || learner.pathway));
      setEditStageAreas(applicable);
    }).catch(() => setEditStageAreas([]));
  }, [viewAreas, learner]); // eslint-disable-line

  // Mandatory subjects are always registered — force them selected once the
  // stage subjects load (covers legacy regs missing a mandatory area).
  useEffect(() => {
    if (!editStageAreas.length) return;
    const mand = editStageAreas.filter(a => a.isMandatory).map(a => a.areaCode);
    setEditSelected(sel => [...new Set([...sel, ...mand])]);
  }, [editStageAreas]);

  const toggleArea = (code) => {
    // Mandatory areas cannot be unchecked
    const isMandatory = stageAreas.find(a => a.areaCode === code)?.isMandatory;
    if (isMandatory) return;
    setNewForm(f => ({
      ...f,
      selectedAreas: f.selectedAreas.includes(code)
        ? f.selectedAreas.filter(c => c !== code)
        : [...f.selectedAreas, code]
    }));
  };

  const handleCreate = async () => {
    if (!newForm.academicYear) { setError('Select academic year'); return; }
    if (!newForm.term)         { setError('Select term'); return; }
    setSaving(true); setError('');
    try {
      const areas = newForm.selectedAreas.map(code => {
        const master    = allAreas.find(x => x.areaCode === code);
        const stageAssn = stageAreas.find(x => x.areaCode === code);
        return {
          areaCode:    code,
          areaName:    master?.areaName || stageAssn?.areaName || code,
          isMandatory: stageAssn?.isMandatory ?? true,
          pathway:     stageAssn?.pathway || 'Common',
          isExaminable:stageAssn?.isExaminable !== false
        };
      });
      await axios.post(`${API}/api/academics/term-registrations`, {
        admNo:        learner.admNo,
        academicYear: newForm.academicYear,
        term:         parseInt(newForm.term, 10),
        stageCode:    learner.stage,
        pathway:      learner.pathway || '',
        studentType:  learner.studentType || 'Boarder',
        learningAreas: areas
      }, { headers });
      setCreating(false);
      fetchData();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to save registration';
      setError(msg);
      setPopup(msg);   // pop it up so a duplicate year/term is impossible to miss
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (id) => { setDeleteRegDlg(id); };
  const confirmDeleteReg = async () => {
    const id = deleteRegDlg;
    setDeleteRegDlg(null);
    try {
      await axios.delete(`${API}/api/academics/term-registrations/${id}`, { headers });
      fetchData();
    } catch { setError('Delete failed'); }
  };

  const handleGenerateInvoice = async () => {
    if (!genReg) return;
    setGenerating(true); setError('');
    try {
      const res = await axios.post(`${API}/api/finance/invoices/generate-term`, {
        admNo:        learner.admNo,
        academicYear: genReg.academicYear,
        term:         genReg.term,
        stageCode:    genReg.stageCode,
        studentType:  genReg.studentType,
      }, { headers });
      const { count = 0, creditApplied = 0, clearedCount = 0 } = res.data || {};
      let m = `Generated ${count} invoice${count === 1 ? '' : 's'} for ${genReg.academicYear} · Term ${genReg.term}.`;
      if (creditApplied > 0) {
        m += ` ${sym()} ${fmt(creditApplied)} auto-applied from overpayment` +
             (clearedCount ? ` across ${clearedCount} item${clearedCount === 1 ? '' : 's'}` : '') + '.';
      }
      setOkMsg(m);
      setInvoicedTerms(prev => new Set([...prev, `${genReg.academicYear}/Term ${genReg.term}`]));
      setGenOpen(false);
      setGenReg(null);
      setGenGlAccount('');
    } catch (err) {
      setError(err.response?.data?.error || 'Invoice generation failed');
    } finally {
      setGenerating(false);
    }
  };

  // A registration's learning areas can be edited only while it's open (Active).
  // Once the term is closed (Completed) or the learner withdrawn, they're locked.
  const canEditAreas = viewAreas?.status === 'Active';

  const toggleEditArea = (code) => {
    // Mandatory subjects cannot be removed.
    if (editStageAreas.find(a => a.areaCode === code)?.isMandatory) return;
    setEditSelected(sel =>
      sel.includes(code) ? sel.filter(c => c !== code) : [...sel, code]);
  };

  const saveEditAreas = async () => {
    if (!viewAreas) return;
    setSavingAreas(true); setAreasErr('');
    try {
      const areas = editSelected.map(code => {
        const stageAssn = editStageAreas.find(x => x.areaCode === code);
        const existing  = (viewAreas.learningAreas || []).find(x => x.areaCode === code);
        const master    = allAreas.find(x => x.areaCode === code);
        return {
          areaCode:    code,
          areaName:    stageAssn?.areaName || existing?.areaName || master?.areaName || code,
          isMandatory: stageAssn?.isMandatory ?? existing?.isMandatory ?? false,
          pathway:     stageAssn?.pathway || existing?.pathway || 'Common',
          isExaminable: (stageAssn?.isExaminable ?? existing?.isExaminable) !== false,
        };
      });
      await axios.put(`${API}/api/academics/term-registrations/${viewAreas._id}`,
        { learningAreas: areas }, { headers });
      setOkMsg('Learning areas updated.');
      setViewAreas(null);
      fetchData();
    } catch (err) {
      setAreasErr(err.response?.data?.error || 'Failed to update learning areas');
    } finally {
      setSavingAreas(false);
    }
  };

  const statusColor = { Active: 'success', Completed: 'info', Withdrawn: 'default' };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle component="div">
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h6" lineHeight={1}>Term Registrations</Typography>
            <Typography variant="caption" color="text.secondary">
              {learner?.name} · {learner?.admNo}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose}><Close /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

        <Snackbar
          open={!!popup}
          autoHideDuration={7000}
          onClose={() => setPopup('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert severity="error" variant="filled" onClose={() => setPopup('')} sx={{ maxWidth: 460 }}>
            {popup}
          </Alert>
        </Snackbar>

        <Snackbar
          open={!!okMsg}
          autoHideDuration={7000}
          onClose={() => setOkMsg('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert severity="success" variant="filled" onClose={() => setOkMsg('')} sx={{ maxWidth: 480 }}>
            {okMsg}
          </Alert>
        </Snackbar>

        {loading ? (
          <Box textAlign="center" py={2}><CircularProgress size={28} /></Box>
        ) : (
          <>
            {/* Existing registrations */}
            {registrations.length === 0 && !creating ? (
              <Typography variant="body2" color="text.secondary">
                No term registrations yet.
              </Typography>
            ) : (
              <Table size="small" sx={{ mb: 2 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell><strong>Year</strong></TableCell>
                    <TableCell align="center"><strong>Term</strong></TableCell>
                    <TableCell><strong>Subjects</strong></TableCell>
                    <TableCell align="right"><strong>Balance ({sym()})</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell align="center"><strong>Invoice</strong></TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {registrations.map(reg => (
                    <TableRow key={reg._id} hover>
                      <TableCell><strong>{reg.academicYear}</strong></TableCell>
                      <TableCell align="center">Term {reg.term}</TableCell>
                      <TableCell>
                        <Tooltip title={reg.status === 'Active' ? 'View / edit subjects' : 'View subjects'}>
                          <Chip
                            label={`${reg.learningAreas?.length || 0} subjects`}
                            size="small" variant="outlined" color="primary"
                            sx={{ cursor: 'pointer' }}
                            onClick={() => setViewAreas(reg)}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        {(() => {
                          const key = `${reg.academicYear}/Term ${reg.term}`;
                          const b = termBalanceMap[key];
                          if (b) {
                            const balance = b.billed - b.paid;
                            return (
                              <Box>
                                <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>
                                  Billed: {fmt(b.billed)}
                                </Typography>
                                <Chip
                                  label={`Due: ${fmt(balance)}`}
                                  color={balance > 0 ? 'error' : 'success'}
                                  size="small" variant="outlined"
                                />
                              </Box>
                            );
                          }
                          return (
                            <Typography variant="body2" color="text.secondary">
                              {fmt(reg.totalFees)}
                            </Typography>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <Chip label={reg.status}
                          color={statusColor[reg.status] || 'default'}
                          size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="center">
                        {invoicedTerms.has(`${reg.academicYear}/Term ${reg.term}`) ? (
                          <Tooltip title="Invoices generated">
                            <CheckCircle fontSize="small" color="success" />
                          </Tooltip>
                        ) : (
                          <Tooltip title="Generate Term Invoice">
                            <IconButton size="small" color="primary"
                              onClick={() => { setGenReg(reg); setGenGlAccount(''); setError(''); setGenOpen(true); }}>
                              <RequestQuote fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" color="error"
                          onClick={() => handleDelete(reg._id)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {/* New registration form */}
            {creating && (
              <Box border={1} borderColor="divider" borderRadius={1} p={2} mt={1}>
                <Typography variant="subtitle2" mb={1} color="primary">New Registration</Typography>
                <Box display="flex" gap={2} mb={2} flexWrap="wrap">
                  <TextField select size="small" label="Academic Year *"
                    value={newForm.academicYear}
                    onChange={e => setNewForm(f => ({ ...f, academicYear: e.target.value, selectedAreas: [] }))}
                    sx={{ minWidth: 140 }}>
                    <MenuItem value=""><em>Select</em></MenuItem>
                    {years.map(y => (
                      <MenuItem key={y.year} value={y.year}>
                        {y.year}{y.isActive ? ' (Active)' : ''}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField select size="small" label="Term *"
                    value={newForm.term}
                    onChange={e => setNewForm(f => ({ ...f, term: e.target.value, selectedAreas: [] }))}
                    sx={{ minWidth: 100 }}>
                    <MenuItem value=""><em>Select</em></MenuItem>
                    {[1, 2, 3].map(t => <MenuItem key={t} value={t}>Term {t}</MenuItem>)}
                  </TextField>
                </Box>

                {matchedTemplate && (
                  <Alert severity="info" sx={{ mb: 1.5 }}>
                    Fee template found: <strong>{sym()} {fmt(matchedTemplate.totalAmount)}</strong> ({matchedTemplate.studentType})
                  </Alert>
                )}
                {newForm.academicYear && newForm.term && !matchedTemplate && (
                  <Alert severity="warning" sx={{ mb: 1.5 }}>
                    No fee template for {learner?.stage} · Term {newForm.term} · {learner?.studentType}
                  </Alert>
                )}

                {/* Learning Areas — only show once both year and term are selected */}
                {newForm.academicYear && newForm.term && (() => {
                  const useStage = stageAreas.length > 0;
                  const displayList = useStage ? stageAreas : allAreas;
                  if (displayList.length === 0) return null;
                  // isMandatory only meaningful from stage setup; master list has no isMandatory
                  const mandatory = useStage ? displayList.filter(a => a.isMandatory !== false) : [];
                  const optional  = useStage ? displayList.filter(a => a.isMandatory === false)  : displayList;
                  return (
                    <>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                        {useStage
                          ? `${mandatory.length} mandatory · ${optional.length} optional (from stage setup)`
                          : 'No stage setup — select learning areas manually'}
                      </Typography>
                      <Box maxHeight={200} overflow="auto" border={1} borderColor="divider" borderRadius={1} p={1}>
                        {displayList.map(area => {
                          // Only lock mandatory when using stage assignments
                          const isMand = useStage && area.isMandatory !== false;
                          const checked = newForm.selectedAreas.includes(area.areaCode);
                          return (
                            <Box key={area.areaCode} display="flex" alignItems="center" gap={0.5}
                              onClick={() => toggleArea(area.areaCode)}
                              sx={{ cursor: isMand ? 'default' : 'pointer', py: 0.25 }}>
                              <Checkbox size="small" checked={checked} onChange={() => {}} disabled={isMand} />
                              <Typography variant="body2" flex={1}>{area.areaName}</Typography>
                              <Typography variant="caption" color="text.secondary">{area.areaCode}</Typography>
                              {isMand && (
                                <Chip label="Mandatory" size="small" color="primary" variant="outlined"
                                  sx={{ ml: 0.5, height: 16, fontSize: 9 }} />
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                      <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
                        {newForm.selectedAreas.length} selected
                      </Typography>
                    </>
                  );
                })()}

                <Box display="flex" gap={1} mt={2}>
                  <Button size="small" variant="contained" onClick={handleCreate} disabled={saving}>
                    {saving ? 'Saving…' : 'Save Registration'}
                  </Button>
                  <Button size="small" onClick={() => setCreating(false)}>Cancel</Button>
                </Box>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {!creating && (
          <Button startIcon={<Add />} onClick={() => setCreating(true)} variant="outlined" size="small">
            Register for Term
          </Button>
        )}
        <Box flex={1} />
        <Button onClick={onClose} variant="outlined">Close</Button>
      </DialogActions>

      {/* Learning Areas sub-dialog — view (locked) or edit (add/remove optional
          subjects) depending on whether the term registration is still open. */}
      <Dialog open={Boolean(viewAreas)} onClose={() => setViewAreas(null)} maxWidth="xs" fullWidth>
        <DialogTitle component="div">
          <Typography variant="h6" component="div" lineHeight={1}>
            {canEditAreas ? 'Edit Learning Areas' : 'Learning Areas'}
          </Typography>
          <Typography variant="caption" component="div" color="text.secondary">
            {viewAreas?.academicYear} · Term {viewAreas?.term}
            {viewAreas?.pathway ? ` · ${viewAreas.pathway}` : ''}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {areasErr && <Alert severity="error" sx={{ mb: 1 }}>{areasErr}</Alert>}

          {!canEditAreas ? (
            // Locked view — registration is Completed/Withdrawn (term closed).
            <>
              <Alert severity="info" sx={{ mb: 1 }}>
                This registration is <strong>{viewAreas?.status}</strong> — learning areas are locked.
              </Alert>
              {viewAreas?.learningAreas?.length === 0 && (
                <Typography variant="body2" color="text.secondary">No learning areas recorded.</Typography>
              )}
              {viewAreas?.learningAreas?.map((a, i) => (
                <Box key={i} display="flex" alignItems="center" gap={1} py={0.5}>
                  <MenuBook fontSize="small" color="action" />
                  <Typography variant="body2">{a.areaName}</Typography>
                  <Chip label={a.areaCode} size="small" variant="outlined" sx={{ ml: 'auto' }} />
                  {a.isMandatory && <Chip label="Mandatory" size="small" color="primary" variant="outlined" />}
                </Box>
              ))}
            </>
          ) : (() => {
            // Editable — mandatory locked & checked, optional toggleable. Areas
            // on the reg that are no longer in the stage's setup are shown last
            // (removable) so they can be cleaned up.
            const stageCodes = new Set(editStageAreas.map(a => a.areaCode));
            const extras = (viewAreas?.learningAreas || []).filter(a => !stageCodes.has(a.areaCode));
            const rows = [
              ...editStageAreas.map(a => ({
                areaCode: a.areaCode, areaName: a.areaName,
                isMandatory: !!a.isMandatory, inStage: true,
              })),
              ...extras.map(a => ({
                areaCode: a.areaCode, areaName: a.areaName,
                isMandatory: false, inStage: false,
              })),
            ];
            const mandCount = rows.filter(r => r.isMandatory).length;
            const optCount  = rows.length - mandCount;
            return (
              <>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  {mandCount} mandatory · {optCount} optional — tick optional subjects to add, untick to remove.
                </Typography>
                {rows.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No subjects defined for this stage/year.
                  </Typography>
                )}
                <Box maxHeight={260} overflow="auto" border={1} borderColor="divider" borderRadius={1} p={1}>
                  {rows.map(r => {
                    const checked = editSelected.includes(r.areaCode);
                    return (
                      <Box key={r.areaCode} display="flex" alignItems="center" gap={0.5}
                        onClick={() => toggleEditArea(r.areaCode)}
                        sx={{ cursor: r.isMandatory ? 'default' : 'pointer', py: 0.25 }}>
                        <Checkbox size="small" checked={checked} onChange={() => {}} disabled={r.isMandatory} />
                        <Typography variant="body2" flex={1}>{r.areaName}</Typography>
                        <Typography variant="caption" color="text.secondary">{r.areaCode}</Typography>
                        {r.isMandatory && (
                          <Chip label="Mandatory" size="small" color="primary" variant="outlined"
                            sx={{ ml: 0.5, height: 16, fontSize: 9 }} />
                        )}
                        {!r.inStage && (
                          <Chip label="Not in stage" size="small" color="warning" variant="outlined"
                            sx={{ ml: 0.5, height: 16, fontSize: 9 }} />
                        )}
                      </Box>
                    );
                  })}
                </Box>
                <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
                  {editSelected.length} selected
                </Typography>
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewAreas(null)} variant="outlined">Close</Button>
          {canEditAreas && (
            <Button onClick={saveEditAreas} variant="contained" disabled={savingAreas}>
              {savingAreas ? 'Saving…' : 'Save Changes'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Generate Term Invoice dialog */}
      <Dialog open={genOpen} onClose={() => setGenOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle component="div">
          <Typography variant="h6" component="div" lineHeight={1}>Generate Term Invoice</Typography>
          {genReg && (
            <Typography variant="caption" component="div" color="text.secondary">
              {genReg.academicYear} · Term {genReg.term} · {genReg.stageCode} · {genReg.studentType}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
          {genReg && (() => {
            const st = genReg.studentType || learner?.studentType || 'Boarder';
            const tmpl = templates.find(t =>
              t.year === genReg.academicYear &&
              t.stageCode === genReg.stageCode &&
              t.term === genReg.term &&
              (t.studentType === st || t.studentType === 'All')
            );
            return (
              <>
                {!tmpl ? (
                  <Alert severity="warning" sx={{ mb: 1.5 }}>
                    No fee template found for {genReg.stageCode} · Term {genReg.term} · {st}
                  </Alert>
                ) : (
                  <>
                    <Table size="small" sx={{ mb: 1.5 }}>
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell><strong>Fee Item</strong></TableCell>
                          <TableCell align="right"><strong>Amount ({sym()})</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {tmpl.items.filter(i => i.amount > 0).map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{item.itemName}</TableCell>
                            <TableCell align="right">{fmt(item.amount)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell><strong>Total</strong></TableCell>
                          <TableCell align="right"><strong>{fmt(tmpl.totalAmount)}</strong></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                      Each fee item posts <strong>DR Receivables</strong> (from the learner's posting group)
                      / <strong>CR</strong> the fee item's income account — no GL selection needed.
                    </Typography>
                  </>
                )}
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setGenOpen(false); setError(''); }}>Cancel</Button>
          <Button
            variant="contained" color="primary"
            onClick={handleGenerateInvoice}
            disabled={generating || !genReg}
          >
            {generating ? 'Generating…' : 'Generate Invoices'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Registration */}
      <Dialog open={Boolean(deleteRegDlg)} onClose={() => setDeleteRegDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Registration</DialogTitle>
        <DialogContent>
          <Typography>
            Delete this term registration? The learner will need to be re-registered.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteRegDlg(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDeleteReg}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

// ── Main List ─────────────────────────────────────────────────────────────────
const LearnersList = () => {
  const navigate = useNavigate();
  const [learners,     setLearners]     = useState([]);
  const [stages,       setStages]       = useState([]);
  const [error,        setError]        = useState('');
  const [searchTerm,   setSearchTerm]   = useState('');
  const [showFilters,  setShowFilters]  = useState(false);
  const [stageFilter,  setStageFilter]  = useState('');
  const [statusFilter, setStatusFilter] = useState('Active');
  const [typeFilter,   setTypeFilter]   = useState('');
  const [order,        setOrder]        = useState('asc');
  const [orderBy,      setOrderBy]      = useState('admNo');
  const [page,         setPage]         = useState(0);
  const [rowsPerPage,  setRowsPerPage]  = useState(10);

  // Dialogs
  const [viewLearner,      setViewLearner]      = useState(null);   // learner card
  const [formOpen,         setFormOpen]         = useState(false);  // create/edit form
  const [editLearner,      setEditLearner]      = useState(null);
  const [formInitialTab,   setFormInitialTab]   = useState(0);      // 3 = open on Guardians tab
  const [termRegOpen,      setTermRegOpen]      = useState(false);  // term reg dialog
  const [deleteLearnerDlg, setDeleteLearnerDlg] = useState(null);  // stores learner object

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const fetchStages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/stages`, { headers });
      setStages(res.data);
    } catch { setStages([]); }
  }, []); // eslint-disable-line

  const fetchLearners = useCallback(async () => {
    try {
      // Learners must always load. Balances are an enrichment that needs the
      // finance module — fetch them only when permitted, and never let a finance
      // 403 reject the learners load (a teacher/non-finance user still gets the
      // list, just without balances).
      const learnersRes = await axios.get(`${API}/api/learners`, { headers });
      let balances = {};
      if (canView('finance')) {
        try {
          const balancesRes = await axios.get(`${API}/api/finance/learners/balances`, { headers });
          balances = balancesRes.data || {};
        } catch { /* no finance access — show learners without balances */ }
      }
      setLearners((learnersRes.data?.data || learnersRes.data || []).map(l => ({ ...l, balance: balances[l.admNo] || 0 })));
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch learners');
    }
  }, []); // eslint-disable-line

  useEffect(() => { fetchLearners(); fetchStages(); }, [fetchLearners, fetchStages]);
  useEffect(() => { setPage(0); }, [searchTerm, stageFilter, statusFilter, typeFilter]);

  const filteredLearners = useMemo(() => {
    let f = [...learners];
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      f = f.filter(l => l.name.toLowerCase().includes(q) || l.admNo.toString().includes(q));
    }
    if (stageFilter)  f = f.filter(l => l.stage === stageFilter);
    if (statusFilter) f = f.filter(l => (l.status || 'Active') === statusFilter);
    if (typeFilter)   f = f.filter(l => l.studentType === typeFilter);
    f.sort((a, b) => {
      const av = a[orderBy], bv = b[orderBy];
      const cmp = typeof av === 'number' ? av - bv : String(av ?? '').localeCompare(String(bv ?? ''));
      return order === 'asc' ? cmp : -cmp;
    });
    return f;
  }, [learners, searchTerm, stageFilter, statusFilter, typeFilter, order, orderBy]);

  const paged = useMemo(() => filteredLearners.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [filteredLearners, page, rowsPerPage]);

  const handleSort = (col) => {
    if (orderBy === col) setOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setOrderBy(col); setOrder('asc'); }
  };

  const handleDelete = (learner) => { setDeleteLearnerDlg(learner); };
  const confirmDeleteLearner = async () => {
    const learner = deleteLearnerDlg;
    setDeleteLearnerDlg(null);
    try {
      await axios.delete(`${API}/api/learners/${learner.admNo}`, { headers });
      setViewLearner(null);
      fetchLearners();
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed');
    }
  };

  const handleFormClose = (saved) => {
    setFormOpen(false);
    if (saved) { setViewLearner(null); fetchLearners(); }
  };

  const handleExport = () => {
    const rows = filteredLearners.map(l => [
      l.admNo, l.name, l.stage, l.studentType || '',
      l.parentEmail, (l.balance ?? 0).toFixed(2)
    ]);
    const csv = [['Adm No', 'Name', 'Stage', 'Type', 'Email', 'Balance'], ...rows]
      .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\r\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    a.download = `learners_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const stageName = (code) => stages.find(s => s.code === code)?.name || code;

  const STATUS_COLORS = {
    Active: 'success', Graduated: 'info', Transferred: 'warning', Withdrawn: 'error'
  };

  const columns = [
    { key: 'admNo',       label: 'Adm No' },
    { key: 'name',        label: 'Name' },
    { key: 'stage',       label: 'Stage' },
    { key: 'studentType', label: 'Type' },
    { key: 'status',      label: 'Status' },
    { key: 'balance',     label: `Balance (${sym()})` },
  ];

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4, mb: 4 }}>
      <Card elevation={3}>
        <CardContent>
          {/* Header */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Typography variant="h5" color="primary">Learners</Typography>
            <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
              <Tooltip title="Assessment Setup">
                <IconButton onClick={() => navigate('/academics/assessment-setup')} sx={{ bgcolor: '#e0f7fa' }}>
                  <Settings sx={{ color: '#00838f' }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Score Entry">
                <IconButton onClick={() => navigate('/academics/score-entry')} sx={{ bgcolor: '#e8eaf6' }}>
                  <Scoreboard sx={{ color: '#283593' }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Assessment Reports">
                <IconButton onClick={() => navigate('/academics/assessment-report')} sx={{ bgcolor: '#e0f2f1' }}>
                  <BarChart sx={{ color: '#004d40' }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Refresh">
                <IconButton onClick={fetchLearners} sx={{ bgcolor: 'background.paper' }}>
                  <Refresh />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export CSV">
                <span>
                  <IconButton onClick={handleExport} disabled={filteredLearners.length === 0}
                    sx={{ bgcolor: 'background.paper' }}>
                    <Download />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Back to Dashboard">
                <IconButton onClick={() => navigate('/dashboard')} sx={{ bgcolor: 'background.paper' }}>
                  <Home />
                </IconButton>
              </Tooltip>
              <Button variant="contained" onClick={() => { setFormInitialTab(0); setEditLearner(null); setFormOpen(true); }}
                startIcon={<Add />}>
                Admit Learner
              </Button>
            </Box>
          </Box>

          {/* Search + filter toggle */}
          <Box display="flex" gap={1} alignItems="center" mb={1}>
            <TextField size="small" label="Search by name or admission no" value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)} sx={{ flex: 1 }}
              InputProps={{ endAdornment: <Search color="action" /> }} />
            <Tooltip title="Filters">
              <IconButton onClick={() => setShowFilters(v => !v)} color={showFilters ? 'primary' : 'default'}>
                <FilterList />
              </IconButton>
            </Tooltip>
          </Box>

          <Collapse in={showFilters}>
            <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
              <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
                <TextField select size="small" label="Status" value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)} sx={{ minWidth: 150 }}>
                  <MenuItem value="">All Statuses</MenuItem>
                  {['Active', 'Graduated', 'Transferred', 'Withdrawn'].map(s =>
                    <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Stage" value={stageFilter}
                  onChange={e => setStageFilter(e.target.value)} sx={{ minWidth: 160 }}>
                  <MenuItem value="">All Stages</MenuItem>
                  {stages.map(s => <MenuItem key={s.code} value={s.code}>{s.code} — {s.name}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Student Type" value={typeFilter}
                  onChange={e => setTypeFilter(e.target.value)} sx={{ minWidth: 150 }}>
                  <MenuItem value="">All Types</MenuItem>
                  {['Boarder', 'Day Scholar'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </TextField>
                <Button size="small" variant="outlined"
                  onClick={() => { setStatusFilter(''); setStageFilter(''); setTypeFilter(''); }}>
                  Clear Filters
                </Button>
              </Box>
            </Paper>
          </Collapse>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {filteredLearners.length === 0 ? (
            <Typography color="text.secondary">No learners found.</Typography>
          ) : (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    {columns.map(col => (
                      <TableCell key={col.key}>
                        <TableSortLabel active={orderBy === col.key}
                          direction={orderBy === col.key ? order : 'asc'}
                          onClick={() => handleSort(col.key)}>
                          <strong>{col.label}</strong>
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paged.map(l => (
                    <TableRow key={l.admNo} hover sx={{ cursor: 'pointer' }}
                      onClick={() => setViewLearner(l)}>
                      <TableCell>{l.admNo}</TableCell>
                      <TableCell><strong>{l.name}</strong></TableCell>
                      <TableCell>
                        <Typography variant="body2">{l.stage}</Typography>
                        <Typography variant="caption" color="text.secondary">{stageName(l.stage)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={l.studentType || '—'} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={l.status || 'Active'}
                          color={STATUS_COLORS[l.status || 'Active'] || 'default'}
                          size="small" variant="outlined"
                        />
                      </TableCell>
                      <TableCell onClick={e => e.stopPropagation()}>
                        <Tooltip title="View ledger">
                          <Chip
                            label={`${sym()} ${fmt(l.balance)}`}
                            color={(l.balance ?? 0) > 0 ? 'error' : 'success'}
                            size="small" variant="outlined"
                            onClick={() => navigate(`/ledger/${l.admNo}`)}
                            sx={{ cursor: 'pointer' }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell onClick={e => e.stopPropagation()}>
                        <Tooltip title="Edit">
                          <IconButton size="small" color="primary"
                            onClick={() => { setFormInitialTab(0); setEditLearner(l); setFormOpen(true); }}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Bill">
                          <IconButton size="small" color="success"
                            onClick={() => navigate('/billing', { state: { learnerAdmNo: l.admNo, stage: l.stage } })}>
                            <Receipt fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Payment">
                          <IconButton size="small" color="info"
                            onClick={() => navigate('/payments/receipt', { state: { learnerAdmNo: l.admNo } })}>
                            <Payment fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination component="div" count={filteredLearners.length}
                page={page} onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[5, 10, 25, 50]} />
            </>
          )}
        </CardContent>
      </Card>

      {/* Learner Card Dialog */}
      <Dialog open={Boolean(viewLearner)} onClose={() => setViewLearner(null)} maxWidth="sm" fullWidth>
        <LearnerCard
          learner={viewLearner}
          stages={stages}
          onClose={() => setViewLearner(null)}
          onEdit={() => { setFormInitialTab(0); setEditLearner(viewLearner); setFormOpen(true); setViewLearner(null); }}
          onManageGuardians={() => { setFormInitialTab(3); setEditLearner(viewLearner); setFormOpen(true); setViewLearner(null); }}
          onDelete={handleDelete}
          onBill={(admNo, stage) => navigate('/billing', { state: { learnerAdmNo: admNo, stage } })}
          onPay={(admNo) => navigate('/payments/receipt', { state: { learnerAdmNo: admNo } })}
          onTermReg={() => setTermRegOpen(true)}
          onResults={(l) => navigate('/academics/assessment-report', { state: { learnerAdmNo: l.admNo, learnerName: l.name } })}
          onAnalytics={(l) => navigate('/academics/lernalytics', { state: { learnerAdmNo: l.admNo } })}
        />
      </Dialog>

      {/* Term Registration Dialog */}
      <TermRegDialog
        open={termRegOpen}
        learner={viewLearner}
        onClose={() => setTermRegOpen(false)}
      />

      {/* Create / Edit Form */}
      <LearnersForm
        open={formOpen}
        learner={editLearner}
        initialTab={formInitialTab}
        onClose={handleFormClose}
      />

      {/* Confirm Delete Learner */}
      <Dialog open={Boolean(deleteLearnerDlg)} onClose={() => setDeleteLearnerDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Learner</DialogTitle>
        <DialogContent>
          <Typography>
            Permanently delete <strong>{deleteLearnerDlg?.name}</strong> ({deleteLearnerDlg?.admNo})? All records will be removed and this cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteLearnerDlg(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDeleteLearner}>Delete Permanently</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LearnersList;
